import type { HttpRequestConfig, HttpResponse } from './types'

/**
 * Error thrown by the http client for network failures and non-2xx responses.
 *
 * Mirrors the shape of `AxiosError` closely enough that existing call-sites that
 * read `error.response.{data,status,headers}` keep working after the migration
 * from axios to native fetch. The `isAxiosError` flag is preserved purely for
 * back-compat with any code still doing `error.isAxiosError` checks.
 */
export class HttpError<T = any> extends Error {
  public config: HttpRequestConfig
  public response?: HttpResponse<T>
  public status?: number
  public code?: string
  public cause?: unknown
  public isAxiosError = true

  constructor(message: string, config: HttpRequestConfig, response?: HttpResponse<T>, code?: string, cause?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.config = config
    this.response = response
    this.status = response?.status
    this.code = code
    // Preserve the underlying error (e.g. undici's `TypeError: fetch failed`)
    // so Sentry can report the full linked-error chain.
    if (cause !== undefined) this.cause = cause
    // Restore prototype chain (TS target ES5/ES2015 interop)
    Object.setPrototypeOf(this, HttpError.prototype)
  }
}

/** Type guard, replacing `axios.isAxiosError`. */
export function isHttpError(error: unknown): error is HttpError {
  return (
    error instanceof HttpError || (typeof error === 'object' && error !== null && (error as any).isAxiosError === true)
  )
}
